// Métadonnées d'un enregistrement EN COURS, persistées dès le démarrage pour
// survivre à un crash / force-quit / kill mémoire OS — c'est le cœur de la
// récup à FROID (Tranche B, incident 2026-05-29).
//
// Le natif finalise un .m4a valide sur interruption ; mais si l'app MEURT avant
// de le sauver, les refs mémoire de CaptureScreen (projet, type lu de l'URL,
// meetingRef, batchExtraction de sessionStorage) sont perdues → le fichier
// devient un orphelin non rattachable. On écrit donc ces métadonnées dans
// Preferences (UserDefaults natif, durable) au démarrage de l'enregistrement, et
// on ACQUITTE l'entrée de l'enregistrement concerné au stop / cancel /
// sauvegarde réussie — une entrée à la fois, jamais la clé entière : plusieurs
// audios peuvent attendre (voir « PLUSIEURS audios en attente » plus bas). Au
// prochain lancement, le scan reconstruit la Capture à partir d'ici. Voir
// [[project_audio_fix_inflight]].

import { buildCapture } from "./build-capture";
import type { Capture, CaptureType, MeetingContext } from "./schema";
import type { KeyValueStorage } from "./storage";

export const PENDING_RECORDING_KEY = "captureBesoins.pendingRecording";

// Clé localStorage du dernier projet sélectionné (posée par Home) — repli quand
// les métadonnées persistées manquent (orphelin d'avant cette fonctionnalité).
export const SELECTED_PROJECT_KEY = "captureBesoins.selectedProjectId";

export interface PendingRecordingMeta {
  startedAt: string; // ISO 8601
  // NULLABLE à dessein (incident 2026-08-17) : un enregistrement peut démarrer
  // sans projet résolu, ou voir son projet disparaître EN COURS de route. Le
  // projet est du MÉTIER ; l'audio, lui, doit rester traçable sans lui. Avant,
  // on écrivait `?? ""` et `parsePendingRecording` rejetait la breadcrumb
  // entière : elle existait mais était illisible — donc inutile. Le projet se
  // comble au moment de récupérer (resolvePendingMeta).
  projectId: string | null;
  type: CaptureType;
  title: string | null;
  meetingContext: MeetingContext | null;
  batchExtraction: boolean;
  userId: string;
  // Audio FINALISÉ mais pas encore rattaché à une Capture. Rempli dès que
  // `stopRecording()` a rendu un uri — c'est-à-dire dès le point de NON-RETOUR
  // technique : le natif y efface son propre état de récupération, donc à partir
  // de cet instant la breadcrumb est le SEUL endroit d'où l'uri du .m4a fusionné
  // peut encore être retrouvé. `finalizedDurationMs` = durée du fichier FUSIONNÉ
  // rendue par le natif (le minuteur de l'écran ne couvre que le dernier segment).
  finalizedUri: string | null;
  finalizedDurationMs: number | null;
}

export interface FinalizedRecording {
  uri: string;
  durationMs: number;
}

export function serializePendingRecording(meta: PendingRecordingMeta): string {
  return JSON.stringify(meta);
}

// Grave l'audio finalisé dans la breadcrumb sans rien perdre de la meta de
// départ (projet, type, titre, contexte de rencontre, mode d'extraction).
export function mergeFinalizedRecording(
  meta: PendingRecordingMeta,
  finalized: FinalizedRecording,
): PendingRecordingMeta {
  return { ...meta, finalizedUri: finalized.uri, finalizedDurationMs: finalized.durationMs };
}

// Lit l'audio finalisé d'une breadcrumb. Une durée absente ne doit JAMAIS
// dissimuler l'uri : on rend 0 plutôt que null (une durée fausse se corrige,
// un fichier perdu, non).
export function pendingFinalizedRecording(
  meta: PendingRecordingMeta | null,
): FinalizedRecording | null {
  if (!meta?.finalizedUri) return null;
  return { uri: meta.finalizedUri, durationMs: meta.finalizedDurationMs ?? 0 };
}

// Chaîne non vide, ou null. Une chaîne vide n'est pas une valeur : c'est le
// résidu d'un `?? ""` — précisément ce qui a rendu la breadcrumb illisible.
function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

// Parse + valide la métadonnée persistée. Renvoie null si absente / corrompue /
// incomplète : un champ critique manquant (user, type, départ) = reconstruction
// impossible → on préfère le repli plutôt qu'une Capture invalide. Le PROJET,
// lui, n'est pas critique ici (voir `projectId` ci-dessus) : une breadcrumb sans
// projet reste la trace d'un audio, et une trace illisible ne sert à rien.
export function parsePendingRecording(raw: string | null | undefined): PendingRecordingMeta | null {
  if (!raw) return null;
  try {
    return coercePendingRecording(JSON.parse(raw));
  } catch {
    return null;
  }
}

function coercePendingRecording(value: unknown): PendingRecordingMeta | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  const { type } = o;
  const userId = nonEmptyString(o.userId);
  const startedAt = nonEmptyString(o.startedAt);
  if (!userId || !startedAt) return null;
  if (type !== "rencontre" && type !== "idee_flash") return null;
  return {
    startedAt,
    projectId: nonEmptyString(o.projectId),
    type,
    title: typeof o.title === "string" ? o.title : null,
    meetingContext: (o.meetingContext as MeetingContext | null) ?? null,
    batchExtraction: o.batchExtraction === true,
    userId,
    finalizedUri: nonEmptyString(o.finalizedUri),
    finalizedDurationMs: finiteNumber(o.finalizedDurationMs),
  };
}

// ── PLUSIEURS audios en attente, sous LA MÊME clé ────────────────────────────
//
// Pourquoi la breadcrumb tient désormais une LISTE : tant qu'il n'y avait qu'un
// emplacement, « démarrer une nouvelle prise » alors qu'un audio finalisé
// attendait encore d'être rattaché réécrivait cet emplacement — donc effaçait la
// seule piste vers ce .m4a (l'exacte perte du 2026-08-17). #256 s'en protégeait
// en INTERDISANT le nouveau départ ; on ne se met plus entre quelqu'un et le
// bouton d'enregistrement, alors la place est faite dans la trace elle-même.
// Aucun second emplacement de stockage n'est ajouté : même clé, une entrée par
// audio, au plus UNE prise en cours (celle sans `finalizedUri`).
//
// Compatibilité : une breadcrumb écrite avant (objet unique) se lit comme une
// liste d'une entrée, et on RÉÉCRIT un objet unique tant qu'il n'y a qu'une
// entrée — la forme sur disque ne change que quand deux audios sont vraiment en
// jeu. Une entrée corrompue ne fait pas perdre les autres : chacune est la seule
// piste vers son fichier.
export function parsePendingRecordings(raw: string | null | undefined): PendingRecordingMeta[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  const items = Array.isArray(parsed) ? parsed : [parsed];
  const list: PendingRecordingMeta[] = [];
  for (const item of items) {
    const meta = coercePendingRecording(item);
    if (meta) list.push(meta);
  }
  return list;
}

// `null` = plus rien à garder → l'appelant EFFACE la clé (une clé qui traîne
// avec une liste vide ressemble à une trace, alors qu'il n'y a plus de fichier).
export function serializePendingRecordings(list: PendingRecordingMeta[]): string | null {
  const [only] = list;
  if (!only) return null;
  return list.length === 1 ? serializePendingRecording(only) : JSON.stringify(list);
}

// La prise EN COURS : l'unique entrée dont l'audio n'est pas encore finalisé.
export function currentPendingTake(list: PendingRecordingMeta[]): PendingRecordingMeta | null {
  return list.find((m) => !m.finalizedUri) ?? null;
}

// Les audios FINALISÉS qui attendent un rattachement, du plus ancien au plus
// récent (ordre d'écriture).
export function pendingFinalizedEntries(list: PendingRecordingMeta[]): PendingRecordingMeta[] {
  return list.filter((m) => m.finalizedUri);
}

// L'entrée qui décrit un fichier donné : celle qui porte son uri finalisée,
// sinon la prise en cours — le récupérable NATIF n'a pas d'uri gravée dans la
// breadcrumb, c'est le fichier de la prise ouverte.
export function pendingEntryForFile(
  list: PendingRecordingMeta[],
  uri: string,
): PendingRecordingMeta | null {
  return list.find((m) => m.finalizedUri === uri) ?? currentPendingTake(list);
}

// Ouvre une prise : elle remplace la prise en cours (il n'y en a jamais deux) et
// ne touche PAS aux audios finalisés en attente. C'est ici que se joue
// l'invariant « démarrer une nouvelle prise ne fait jamais perdre l'orphelin ».
export function withPendingTake(
  list: PendingRecordingMeta[],
  meta: PendingRecordingMeta,
): PendingRecordingMeta[] {
  return [...pendingFinalizedEntries(list), meta];
}

// Grave l'audio finalisé sur la prise en cours. Sans prise en cours (trace de
// départ perdue), on AJOUTE une entrée construite depuis `base` — jamais on
// n'écrase une entrée déjà finalisée : ce serait perdre son .m4a.
export function withFinalizedRecording(
  list: PendingRecordingMeta[],
  finalized: FinalizedRecording,
  base: PendingRecordingMeta,
): PendingRecordingMeta[] {
  const take = currentPendingTake(list);
  if (!take) return [...list, mergeFinalizedRecording(base, finalized)];
  return list.map((m) => (m === take ? mergeFinalizedRecording(m, finalized) : m));
}

// Acquitte l'entrée d'un audio qui a fini son chemin (sauvé localement, ou
// abandonné explicitement par l'usager). `uri` inconnue ou `null` = la prise en
// cours (annulation, ou fichier rendu par le natif sans uri gravée). Les AUTRES
// entrées ne sont jamais touchées : chacune est la seule piste vers son fichier.
export function withoutPendingRecording(
  list: PendingRecordingMeta[],
  uri: string | null,
): PendingRecordingMeta[] {
  const target =
    (uri ? list.find((m) => m.finalizedUri === uri) : null) ?? currentPendingTake(list);
  return target ? list.filter((m) => m !== target) : list;
}

// Toute lecture-modification-écriture de la breadcrumb passe par ici : aucun
// appelant n'a ainsi à se rappeler qu'il ne doit pas écraser les entrées des
// autres audios. Les écritures sont SÉRIALISÉES — deux read-modify-write qui se
// croisent perdraient une entrée, c'est-à-dire un .m4a.
export interface PendingRecordingStore {
  list(): Promise<PendingRecordingMeta[]>;
  openTake(meta: PendingRecordingMeta): Promise<void>;
  markFinalized(finalized: FinalizedRecording, base: PendingRecordingMeta): Promise<void>;
  release(uri: string | null): Promise<void>;
}

export function createPendingRecordingStore(kv: KeyValueStorage): PendingRecordingStore {
  const read = async () => parsePendingRecordings(await kv.get(PENDING_RECORDING_KEY));

  let queue: Promise<unknown> = Promise.resolve();
  function mutate(f: (list: PendingRecordingMeta[]) => PendingRecordingMeta[]): Promise<void> {
    const next = queue.then(async () => {
      const raw = serializePendingRecordings(f(await read()));
      if (raw === null) await kv.remove(PENDING_RECORDING_KEY);
      else await kv.set(PENDING_RECORDING_KEY, raw);
    });
    // La file avance même si une écriture échoue (best-effort, jamais de blocage).
    queue = next.catch(() => {});
    return next;
  }

  return {
    list: read,
    openTake: (meta) => mutate((l) => withPendingTake(l, meta)),
    markFinalized: (finalized, base) => mutate((l) => withFinalizedRecording(l, finalized, base)),
    release: (uri) => mutate((l) => withoutPendingRecording(l, uri)),
  };
}

// Métadonnée de repli quand la persistée manque (orphelin d'avant la
// fonctionnalité, ou écriture échouée) mais qu'un fichier est à sauver : projet =
// dernier sélectionné, type par défaut idee_flash. Mieux qu'abandonner l'audio.
// Sans projet (jamais sélectionné), null : buildCapture exige un projectId.
export function fallbackPendingMeta(input: {
  projectId: string | null;
  userId: string;
  startedAt: string;
}): ResolvedPendingMeta | null {
  if (!input.projectId) return null;
  return {
    startedAt: input.startedAt,
    projectId: input.projectId,
    type: "idee_flash",
    title: null,
    meetingContext: null,
    batchExtraction: true,
    userId: input.userId,
    finalizedUri: null,
    finalizedDurationMs: null,
  };
}

// Meta dont le projet est RÉSOLU : le seul état à partir duquel buildCapture
// peut travailler.
export interface ResolvedPendingMeta extends PendingRecordingMeta {
  projectId: string;
}

// Comble le projet d'une breadcrumb : celui inscrit au départ, sinon le dernier
// projet sélectionné. Sans projet résoluble → null, et l'appelant doit alors
// TOUT laisser en place (l'audio reste retrouvable pour un rattachement humain).
export function resolvePendingMeta(
  meta: PendingRecordingMeta | null,
  fallbackProjectId: string | null,
): ResolvedPendingMeta | null {
  if (!meta) return null;
  const projectId = meta.projectId ?? fallbackProjectId;
  if (!projectId) return null;
  return { ...meta, projectId };
}

export type ColdEntryPoint =
  | { kind: "nothing" }
  | { kind: "resume-screen" }
  | { kind: "attach"; file: FinalizedRecording };

// Au réveil de l'app (lancement / retour réseau / 1er plan) : que faire de
// l'audio en rade ? Décision PURE, hors de tout Capacitor.
//
// PRÉCÉDENCE au récupérable NATIF : c'est un fichier encore ouvrable en reprise
// (l'enregistrement a été interrompu, pas arrêté). L'audio FINALISÉ de la
// breadcrumb, lui, ne se reprend PLUS — `stopRecording()` a réinitialisé la
// session native — donc même une rencontre finalisée se RATTACHE au lieu d'être
// proposée en reprise (incident 2026-08-17).
//
// L'entrée est la LISTE des entrées en attente : le récupérable natif appartient
// à la prise EN COURS (c'est SON type qui décide de la reprise), et l'audio
// finalisé rattaché est le PLUS ANCIEN en attente.
export function decideColdEntryPoint(input: {
  nativeRecoverable: FinalizedRecording | null;
  pending: PendingRecordingMeta[];
}): ColdEntryPoint {
  if (input.nativeRecoverable) {
    return currentPendingTake(input.pending)?.type === "rencontre"
      ? { kind: "resume-screen" }
      : { kind: "attach", file: input.nativeRecoverable };
  }
  const [orphan] = pendingFinalizedEntries(input.pending);
  const finalized = pendingFinalizedRecording(orphan ?? null);
  return finalized ? { kind: "attach", file: finalized } : { kind: "nothing" };
}

export type CaptureEntryPoint =
  | { kind: "fresh" }
  | { kind: "offer-orphan"; file: FinalizedRecording; meta: PendingRecordingMeta };

// Ouvrir l'écran de capture avec un audio FINALISÉ encore en attente : on OFFRE
// les deux issues (le sauvegarder / démarrer une nouvelle prise) au lieu de
// décider à la place de l'usager.
//
// Ni auto-sauvegarde — elle plantait la personne venue capter MAINTENANT sur
// l'écran de fin d'une prise de la veille — ni blocage du départ : #256
// interdisait `startFreshRecording` pour ne pas réécrire la seule trace du .m4a,
// ce qui n'est plus nécessaire (la breadcrumb tient plusieurs entrées, la
// nouvelle prise s'AJOUTE sans rien écraser). On ne se met jamais entre
// quelqu'un et le bouton d'enregistrement.
export function decideCaptureEntryPoint(pending: PendingRecordingMeta[]): CaptureEntryPoint {
  const [orphan] = pendingFinalizedEntries(pending);
  const file = pendingFinalizedRecording(orphan ?? null);
  return file && orphan ? { kind: "offer-orphan", file, meta: orphan } : { kind: "fresh" };
}

export type ColdRecoveryDecision =
  | { kind: "recover"; meta: ResolvedPendingMeta }
  | { kind: "skip"; reason: "no-file" | "no-metadata" };

// On RÉCUPÈRE seulement si on a À LA FOIS le fichier (récupérable natif) ET des
// métadonnées (persistées ou de repli) — sinon pas de Capture fidèle possible.
export function decideColdRecovery(input: {
  hasRecoverableFile: boolean;
  meta: ResolvedPendingMeta | null;
}): ColdRecoveryDecision {
  if (!input.hasRecoverableFile) return { kind: "skip", reason: "no-file" };
  if (!input.meta) return { kind: "skip", reason: "no-metadata" };
  return { kind: "recover", meta: input.meta };
}

export interface ColdRecoveryDeps {
  getRecoverable: () => Promise<{ uri: string; durationMs: number } | null>;
  readPendingMetaRaw: () => Promise<string | null>;
  fallbackProjectId: () => string | null;
  saveCapture: (capture: Capture) => Promise<void>;
  clearNativeRecoverable: () => Promise<void>;
  // Acquitte l'entrée du fichier qui vient d'être sauvé — et RIEN d'autre : la
  // breadcrumb peut tenir un AUTRE audio en attente, dont elle est la seule
  // piste (ex. un orphelin qui patiente pendant qu'une nouvelle prise tourne).
  clearPendingMeta: (savedUri: string) => Promise<void>;
  newId: () => string;
  userId: string;
  nowMs: number;
}

// Orchestre la récup à froid (dépendances injectées → testable sans Capacitor).
// ORDRE CRITIQUE : sauver EN LOCAL d'abord (durable), PUIS acquitter le natif et
// la breadcrumb. Cleaner avant le save = perte du fichier si le save échoue —
// l'exact inverse de l'objectif. La re-synchro réseau est faite ensuite par
// resyncPendingCaptures (la Capture est désormais dans le stockage local).
//
// DEUX sources de fichier, dans cet ordre : le récupérable NATIF (interruption,
// fichier non finalisé) puis l'audio FINALISÉ gravé dans la breadcrumb. La
// seconde est la seule qui reste après un `stopRecording()` — le natif y a
// effacé son propre état de récupération (incident 2026-08-17).
export async function recoverColdRecording(
  deps: ColdRecoveryDeps,
): Promise<{ recovered: boolean; reason?: string }> {
  const pending = parsePendingRecordings(await deps.readPendingMetaRaw());
  const [orphan] = pendingFinalizedEntries(pending);
  const recoverable =
    (await deps.getRecoverable()) ?? pendingFinalizedRecording(orphan ?? null) ?? null;
  if (!recoverable) return { recovered: false, reason: "no-file" };

  const fallbackProjectId = deps.fallbackProjectId();
  // L'entrée DU FICHIER, pas « l'entrée » : plusieurs audios peuvent attendre, et
  // reconstruire une Capture avec la meta d'un autre la mal-étiquetterait.
  const meta =
    resolvePendingMeta(pendingEntryForFile(pending, recoverable.uri), fallbackProjectId) ??
    fallbackPendingMeta({
      projectId: fallbackProjectId,
      userId: deps.userId,
      // Sans métadonnées, pas de départ fiable : on rétro-date depuis la durée.
      startedAt: new Date(deps.nowMs - recoverable.durationMs).toISOString(),
    });

  const decision = decideColdRecovery({ hasRecoverableFile: true, meta });
  if (decision.kind === "skip") return { recovered: false, reason: decision.reason };

  const capture = buildCapture({
    id: deps.newId(),
    createdBy: decision.meta.userId,
    projectId: decision.meta.projectId,
    type: decision.meta.type,
    title: decision.meta.title,
    durationMs: recoverable.durationMs,
    audioUri: recoverable.uri,
    keepAudio: true,
    startedAt: new Date(decision.meta.startedAt),
    now: new Date(deps.nowMs),
    meetingContext: decision.meta.meetingContext,
    batchExtraction: decision.meta.batchExtraction,
  });

  await deps.saveCapture(capture);
  await deps.clearNativeRecoverable();
  await deps.clearPendingMeta(recoverable.uri);
  return { recovered: true };
}
